import { Box, Checkbox, FormControlLabel, Stack, Typography, useTheme } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import type { PbjCategoryScore, PbjModelUpsertBody } from "../../../utils/models";

// Category id → the PbjModelUpsertBody field that overrides its scored value. Total/RN
// turnover take a percentage; Admin Turnover takes an integer departure count.
const MANUAL_FIELD_BY_CATEGORY_ID: Record<string, "manualTotalTurnoverPct" | "manualRnTurnoverPct" | "manualAdminTurnoverCount"> = {
	"4": "manualTotalTurnoverPct",
	"5": "manualRnTurnoverPct",
	"6": "manualAdminTurnoverCount",
};

// Per-category PbjModelUpsertBody flags for the "N/A" and "Invalid" scoring adjustments.
// All three turnover measures (Total, RN, Admin) support both flags symmetrically.
type TurnoverAdjustmentFields = {
	na: "totalTurnoverNa" | "rnTurnoverNa" | "adminTurnoverNa";
	invalid: "totalTurnoverInvalid" | "rnTurnoverInvalid" | "adminTurnoverInvalid";
};
const ADJUSTMENT_FIELDS_BY_CATEGORY_ID: Record<string, TurnoverAdjustmentFields> = {
	"4": { na: "totalTurnoverNa", invalid: "totalTurnoverInvalid" },
	"5": { na: "rnTurnoverNa", invalid: "rnTurnoverInvalid" },
	"6": { na: "adminTurnoverNa", invalid: "adminTurnoverInvalid" },
};

type Props = {
	// The category being edited; `null` keeps the dialog closed (parent gate).
	category: PbjCategoryScore | null;
	draft: PbjModelUpsertBody | null;
	onClose: () => void;
	// Keeps the parent's dirty draft in sync with the pending edit.
	onDraftChange: (next: PbjModelUpsertBody) => void;
	// Persist + recompute with the edit so the new score shows without a separate Calculate click.
	onCalculate: (next: PbjModelUpsertBody) => void;
};

// A blank value is legal — it clears the override so the computed value is used.
// A non-blank value must be a finite number in range (pct 0..100, admin integer >= 0);
// anything else surfaces as an explicit error and blocks confirm rather than being
// silently clamped/rounded (the old Math.min/Math.max/Math.round behavior).
const isManualValueValid = (raw: string, isAdmin: boolean): boolean => {
	const trimmed = raw.trim();
	if (trimmed === "") return true;
	const n = Number(trimmed);
	if (!Number.isFinite(n) || n < 0) return false;
	return isAdmin ? Number.isInteger(n) : n <= 100;
};

export const ManualTurnoverDialog: FC<Props> = ({ category, draft, onClose, onDraftChange, onCalculate }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const accent = theme.palette.primary.main;

	const [manualValue, setManualValue] = useState<string>("");
	const [na, setNa] = useState<boolean>(false);
	const [invalid, setInvalid] = useState<boolean>(false);

	const open = category !== null;
	const isAdmin = category?.kind === "admin";
	// All three turnover measures (Total/RN/Admin) expose N/A + Invalid; HPPD does not.
	const supportsAdjustment = category ? category.id in ADJUSTMENT_FIELDS_BY_CATEGORY_ID : false;
	const adjustmentActive = supportsAdjustment && (na || invalid);

	// Seed the dialog-local state from the live draft each time it opens for a category, so a
	// pending (not-yet-Calculated) edit reappears. Intentionally not re-seeding on every draft
	// change — only when the edited category changes — so typing isn't clobbered mid-edit.
	// biome-ignore lint/correctness/useExhaustiveDependencies: seed once per open, not on every draft keystroke.
	useEffect(() => {
		if (!category) return;
		const field = MANUAL_FIELD_BY_CATEGORY_ID[category.id];
		const current = draft && field ? draft[field] : null;
		setManualValue(current === null || current === undefined ? "" : String(current));
		const adj = ADJUSTMENT_FIELDS_BY_CATEGORY_ID[category.id];
		setNa(adj && draft ? (draft[adj.na] ?? false) : false);
		setInvalid(adj && draft ? (draft[adj.invalid] ?? false) : false);
	}, [category]);

	// When the adjustment toggles disable the field, its text is ignored — don't let a stale
	// invalid string block confirm.
	const numberValid = adjustmentActive || isManualValueValid(manualValue, !!isAdmin);

	const handleConfirm = () => {
		if (!draft || !category) {
			onClose();
			return;
		}
		const field = MANUAL_FIELD_BY_CATEGORY_ID[category.id];
		const trimmed = manualValue.trim();
		// Confirm is blocked while invalid, so a non-blank value here is already in-range —
		// parse it verbatim (no clamp/round). Blank clears the override.
		const parsed: number | null = trimmed === "" ? null : Number(trimmed);
		const next: PbjModelUpsertBody = { ...draft, [field]: parsed };
		const adj = ADJUSTMENT_FIELDS_BY_CATEGORY_ID[category.id];
		if (adj) {
			next[adj.na] = na;
			next[adj.invalid] = invalid;
		}
		onDraftChange(next);
		onCalculate(next);
		onClose();
	};

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			dataTestId="pbj-model-manual-turnover-dialog"
			title={category ? t("pbj_model.scoring.manual.dialog.title", { title: category.title }) : ""}
			confirm={t("save")}
			cancel={t("close")}
			confirmButtonVariant="primary"
			confirmDisabled={!draft || !numberValid}
			onConfirm={handleConfirm}
			body={
				category && (
					<Stack spacing={2} sx={{ pt: 1 }}>
						<Typography variant="body2" color="text.secondary">
							{isAdmin ? t("pbj_model.scoring.manual.dialog.help_count") : t("pbj_model.scoring.manual.dialog.help_pct")}
						</Typography>
						<Box>
							<SystemValidationTextField
								fullWidth
								inputType="number"
								label={isAdmin ? t("pbj_model.scoring.manual.input_count") : t("pbj_model.scoring.manual.input_pct")}
								value={manualValue}
								onChange={(e) => setManualValue(e.target.value)}
								placeholder={String(category.rawValue)}
								disabled={adjustmentActive}
								validation={{
									validationCallback: (v: string) => isManualValueValid(v, !!isAdmin),
									helperText: { invalid: isAdmin ? t("pbj_model.scoring.manual.dialog.invalid_count") : t("pbj_model.scoring.manual.dialog.invalid_pct") },
									performValidation: "onlyAfterInitialChange",
									showValidWhenValid: "never",
								}}
								defaultHelperText={t("pbj_model.scoring.manual.dialog.calculate_hint")}
								inputProps={{
									inputProps: isAdmin
										? { min: 0, step: 1, "data-testid": "pbj-model-manual-input" }
										: // CMS turnover bands have 3-decimal cutoffs (e.g. 69.792); `step="any"` lets
											// admins enter the exact percentage without the browser snapping finer-than-step values.
											{ min: 0, max: 100, step: "any", "data-testid": "pbj-model-manual-input" },
								}}
							/>
							<Box sx={{ display: "flex", justifyContent: "flex-end", mt: -0.5 }}>
								<SystemButton
									buttonVariant="borderless"
									size="small"
									disabled={adjustmentActive || manualValue.trim() === ""}
									onClick={() => setManualValue("")}
									noFullWidthMobile
									data-testid="pbj-model-manual-reset"
								>
									{t("pbj_model.scoring.manual.reset")}
								</SystemButton>
							</Box>
						</Box>
						{supportsAdjustment && (
							<Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
								<Typography variant="caption" sx={{ display: "block", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }} color="text.secondary">
									{t("pbj_model.scoring.adjustment.label")}
								</Typography>
								<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
									{t("pbj_model.scoring.adjustment.help")}
								</Typography>
								<Stack>
									<FormControlLabel
										control={
											<Checkbox
												checked={na}
												onChange={(e) => {
													const checked = e.target.checked;
													setNa(checked);
													if (checked) setInvalid(false);
												}}
												sx={{ color: accent, "&.Mui-checked": { color: accent } }}
											/>
										}
										label={t("pbj_model.scoring.na.toggle")}
										slotProps={{ typography: { variant: "body2" } }}
										sx={{ m: 0 }}
										data-testid={`pbj-model-category-${category.id}-na-toggle`}
									/>
									<FormControlLabel
										control={
											<Checkbox
												checked={invalid}
												onChange={(e) => {
													const checked = e.target.checked;
													setInvalid(checked);
													if (checked) setNa(false);
												}}
												sx={{ color: accent, "&.Mui-checked": { color: accent } }}
											/>
										}
										label={t("pbj_model.scoring.invalid.toggle")}
										slotProps={{ typography: { variant: "body2" } }}
										sx={{ m: 0 }}
										data-testid={`pbj-model-category-${category.id}-invalid-toggle`}
									/>
								</Stack>
							</Box>
						)}
					</Stack>
				)
			}
		/>
	);
};
